import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import Button from '../components/Button';
import ChannelPreferencePicker from '../components/ChannelPreferencePicker';
import ExternalRedirect, {
  HOME_REDIRECT_URL,
} from '../components/ExternalRedirect';
import { fetchPreferenceForm, submitPreferenceForm } from '../api/preferences';
import { ChannelConsentState } from '../types/subscribers';

const PreferenceFormPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [emailChannel, setEmailChannel] = useState<ChannelConsentState | null>(
    null,
  );
  const [smsChannel, setSmsChannel] = useState<ChannelConsentState | null>(null);
  const [consentLanguage, setConsentLanguage] = useState<string | null>(null);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedEmail, setSavedEmail] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadPreferences = async () => {
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const preferences = await fetchPreferenceForm(token);
        if (cancelled) {
          return;
        }
        setEmailChannel(preferences.emailChannel);
        setSmsChannel(preferences.smsChannel);
        setConsentLanguage(preferences.consentLanguage ?? null);
      } catch {
        // Missing / invalid token → redirect handled below once loading ends.
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadPreferences();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!token) {
      setError('This preference link is missing a token.');
      return;
    }
    if (!emailChannel || !smsChannel) {
      setError('Unable to save preferences before they have loaded.');
      return;
    }
    if (!consentAccepted) {
      setError('Please confirm the consent statement before saving.');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      const subscriber = await submitPreferenceForm(token, {
        emailChannel,
        smsChannel,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        urlLocation:
          typeof window !== 'undefined' ? window.location.href : null,
      });
      setSavedEmail(subscriber.email);
    } catch (e: any) {
      setError(e?.message || 'Unable to save your preferences.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (savedEmail) {
    return (
      <div className="min-h-screen bg-primary-gray flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-lg rounded-lg bg-white border border-gray-200 p-8 text-center">
          <CheckCircleIcon className="mx-auto h-12 w-12 text-primary" />
          <h1
            data-testid="preferences-saved"
            className="mt-4 font-ivy text-3xl text-gray-900"
          >
            Preferences saved
          </h1>
          <p className="mt-3 text-sm text-gray-600 font-fustat">
            Thanks — we updated marketing preferences for{' '}
            <span className="font-fustat-medium text-gray-900">{savedEmail}</span>.
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-primary-gray flex items-center justify-center px-6 py-12">
        <p className="text-sm text-gray-600 font-fustat">
          Loading your preferences…
        </p>
      </div>
    );
  }

  if (!emailChannel || !smsChannel || !consentLanguage) {
    return <ExternalRedirect to={HOME_REDIRECT_URL} />;
  }

  return (
    <div className="min-h-screen bg-primary-gray flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-lg rounded-lg bg-white border border-gray-200 p-8">
        <img src="/favicon.svg" alt="RetainAI" className="h-10 w-10" />
        <h1 className="mt-5 font-ivy text-3xl text-gray-900">
          Communication preferences
        </h1>
        <p className="mt-2 text-sm text-gray-600 font-fustat">
          Choose how you would like to hear from us on each channel.
        </p>

        <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
          <ChannelPreferencePicker
            label="Email"
            description="Product updates, offers, and campaign emails."
            value={emailChannel}
            onChange={setEmailChannel}
          />
          <ChannelPreferencePicker
            label="SMS"
            description="Text messages about orders, drops, and promotions."
            value={smsChannel}
            onChange={setSmsChannel}
          />

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={consentAccepted}
              onChange={(event) => {
                setConsentAccepted(event.target.checked);
                if (event.target.checked) {
                  setError(null);
                }
              }}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-primary focus:ring-primary"
              required
            />
            <span
              data-testid="consent-language"
              className="text-xs text-gray-500 font-fustat leading-relaxed"
            >
              {consentLanguage}
            </span>
          </label>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 font-fustat">
              {error}
            </div>
          )}

          <Button
            type="submit"
            className="w-full"
            isLoading={isSubmitting}
            disabled={!consentAccepted}
          >
            Save preferences
          </Button>
        </form>
      </div>
    </div>
  );
};

export default PreferenceFormPage;
